import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { FiX, FiHeart, FiChevronLeft, FiChevronRight } from "react-icons/fi";
import { useTranslation } from "react-i18next";
import AlternativeProductsBlock from "../AlternativeProductsBlock";
import ProductTabs from "../product/ProductTabs";
import LeaveCommentSheet from "../product/LeaveCommentSheet";
import "../../styles/ProductSheet.css";
import { apiCatalogReviews } from "../../api/catalog.api";
import { useAuth } from "../../auth/AuthContext";
import { loginWithGoogle } from "../../services/auth.service";
import { trackEvent } from "../../services/amplitude";

const ANIM_MS = 450;
const CLOSE_DRAG_THRESHOLD = 120;
const MAX_DRAG_Y = 420;

function normalizeImages(product) {
  const arr = Array.isArray(product?.images) ? product.images : [];
  const normalized = arr
    .map((x) => ({
      id: String(x?.id ?? ""),
      url: x?.image_url || x?.image_proxy_url || "",
      sequence: Number(x?.sequence ?? 0),
    }))
    .filter((x) => x.id && x.url);

  if (!normalized.length) {
    const url = product?.image_url || product?.image_proxy_url || "";
    if (url) return [{ id: "main", url, sequence: 0 }];
  }

  normalized.sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0));
  return normalized;
}

function normalizeLang(lang) {
  return String(lang || "").toLowerCase().startsWith("en") ? "en" : "uk";
}

function getLocalizedName(product, lang = "uk") {
  const safeLang = normalizeLang(lang);

  if (safeLang === "en") {
    return product?.name_en || product?.name || product?.name_uk || "";
  }

  return product?.name_uk || product?.name || product?.name_en || "";
}

function getLocalizedDescription(product, lang = "uk") {
  const safeLang = normalizeLang(lang);

  if (safeLang === "en") {
    return (
      product?.description_en ||
      product?.description_text ||
      product?.description_uk ||
      ""
    );
  }

  return (
    product?.description_uk ||
    product?.description_text ||
    product?.description_en ||
    ""
  );
}

export default function ProductSheet({
  open,
  onClose,
  onBack = null,
  showBackButton = false,
  product,
  topOffset = 84,
  onToggleFavorite,
  isFavorite = false,
  onAddToCart,
  onOpenProduct,
  initialTab = "info",
  initialOpenComment = false,
}) {
  const { t, i18n } = useTranslation("product");
  const { user, loading: authLoading } = useAuth();

  const sheetRef = useRef(null);
  const sliderRef = useRef(null);
  const contentRef = useRef(null);
  const detailsTrackedRef = useRef("");

  const dragStartYRef = useRef(0);
  const dragDeltaYRef = useRef(0);
  const draggingRef = useRef(false);

  const [activeTab, setActiveTab] = useState(initialTab);
  const [mounted, setMounted] = useState(open);
  const [closing, setClosing] = useState(false);
  const [dragY, setDragY] = useState(0);
  const [reviews, setReviews] = useState([]);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [isLeaveCommentOpen, setIsLeaveCommentOpen] = useState(false);

  const images = useMemo(() => normalizeImages(product), [product]);
  const [imgIndex, setImgIndex] = useState(0);

  const currentLang = normalizeLang(
    i18n.resolvedLanguage || i18n.language || "uk"
  );

  const localizedProduct = useMemo(() => {
    const name = getLocalizedName(product, currentLang);
    const description_text = getLocalizedDescription(product, currentLang);

    return {
      ...product,
      name,
      description_text,
    };
  }, [product, currentLang]);

  useEffect(() => {
    if (!open || !localizedProduct) return;

    const productKey = String(
      localizedProduct?.variant_id ||
        localizedProduct?.product_id ||
        localizedProduct?.id ||
        localizedProduct?.template_id ||
        ""
    );

    if (!productKey) return;

    const trackingKey = `${productKey}:${currentLang}`;
    if (detailsTrackedRef.current === trackingKey) return;

    detailsTrackedRef.current = trackingKey;

    trackEvent("product_details_opened", {
      product_id: localizedProduct?.product_id || localizedProduct?.id || null,
      variant_id: localizedProduct?.variant_id || null,
      template_id: localizedProduct?.template_id || null,
      product_name: localizedProduct?.name || null,
      lang: currentLang,
      categories: Array.isArray(localizedProduct?.categories)
        ? localizedProduct.categories.slice(0, 5)
        : [],
    });
  }, [open, localizedProduct, currentLang]);

  const loadReviews = useCallback(async () => {
    const productId =
      localizedProduct?.template_id ||
      localizedProduct?.id ||
      localizedProduct?.product_id;

    if (!open || !productId) {
      setReviews([]);
      return;
    }

    try {
      setReviewsLoading(true);
      const items = await apiCatalogReviews(productId, currentLang);
      setReviews(items || []);
    } catch (e) {
      console.warn("reviews load error:", e?.message);
      setReviews([]);
    } finally {
      setReviewsLoading(false);
    }
  }, [
    open,
    localizedProduct?.template_id,
    localizedProduct?.id,
    localizedProduct?.product_id,
    currentLang,
  ]);

  useEffect(() => {
    let alive = true;

    (async () => {
      const productId =
        localizedProduct?.template_id ||
        localizedProduct?.id ||
        localizedProduct?.product_id;

      if (!open || !productId) {
        setReviews([]);
        return;
      }

      try {
        setReviewsLoading(true);
        const items = await apiCatalogReviews(productId, currentLang);
        if (!alive) return;
        setReviews(items || []);
      } catch (e) {
        console.warn("reviews load error:", e?.message);
        if (!alive) return;
        setReviews([]);
      } finally {
        if (alive) setReviewsLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [
    open,
    localizedProduct?.template_id,
    localizedProduct?.id,
    localizedProduct?.product_id,
    currentLang,
  ]);

  function formatPriceUAH(price) {
    const n = Number(price || 0);
    const locale = currentLang === "uk" ? "uk-UA" : "en-US";

    const formatted = new Intl.NumberFormat(locale, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(n);

    return `${t("currency_symbol")}${formatted}`;
  }

  useEffect(() => {
    setImgIndex(0);
    if (sliderRef.current) sliderRef.current.scrollLeft = 0;
  }, [localizedProduct?.id, open]);

  useEffect(() => {
    setActiveTab(initialTab || "info");
    contentRef.current?.scrollTo?.({ top: 0, behavior: "smooth" });
  }, [localizedProduct?.id, initialTab]);

  useEffect(() => {
    if (open) {
      setMounted(true);
      setClosing(false);
      setDragY(0);
      draggingRef.current = false;
      dragDeltaYRef.current = 0;
      return;
    }

    if (mounted) {
      setClosing(true);
      const tmr = setTimeout(() => {
        setMounted(false);
        setClosing(false);
        setDragY(0);
      }, ANIM_MS);
      return () => clearTimeout(tmr);
    }
  }, [open, mounted]);

  useEffect(() => {
    if (!open || !initialOpenComment) return;
    if (!user) return;

    setActiveTab("reviews");
    setIsLeaveCommentOpen(true);
  }, [open, initialOpenComment, user, localizedProduct?.id]);

  function clamp(n, min, max) {
    return Math.max(min, Math.min(n, max));
  }

  function handleDragStart(clientY) {
    if (closing) return;
    draggingRef.current = true;
    dragStartYRef.current = clientY;
    dragDeltaYRef.current = 0;
  }

  function handleDragMove(clientY) {
    if (!draggingRef.current) return;

    const delta = clientY - dragStartYRef.current;
    const next = clamp(delta, 0, MAX_DRAG_Y);

    dragDeltaYRef.current = next;
    setDragY(next);
  }

  function handleDragEnd() {
    if (!draggingRef.current) return;

    draggingRef.current = false;
    const shouldClose = dragDeltaYRef.current > CLOSE_DRAG_THRESHOLD;

    if (shouldClose) {
      setDragY(0);
      onClose?.();
      return;
    }

    dragDeltaYRef.current = 0;
    setDragY(0);
  }

  function onPointerDown(e) {
    handleDragStart(e.clientY);
  }

  function onPointerMove(e) {
    handleDragMove(e.clientY);
  }

  function onPointerUp() {
    handleDragEnd();
  }

  function onPointerCancel() {
    handleDragEnd();
  }

  async function handleOpenLeaveComment() {
    if (authLoading) return;

    if (!user) {
      try {
        sessionStorage.setItem(
          "pending_review_product",
          JSON.stringify(localizedProduct)
        );
        sessionStorage.setItem("pending_review_tab", "reviews");
        sessionStorage.setItem("pending_review_open_comment", "true");
        await loginWithGoogle();
      } catch (e) {
        console.error("login before comment error:", e);
      }
      return;
    }

    setActiveTab("reviews");
    setIsLeaveCommentOpen(true);
  }

  function handleBuyClick(e) {
  e.stopPropagation();

  const vid =
    localizedProduct?.variant_id ||
    localizedProduct?.product_id ||
    localizedProduct?.id;

  if (!vid) return;

  trackEvent("cart_item_added", {
    product_id: localizedProduct?.product_id || localizedProduct?.id || null,
    product_name: localizedProduct?.name || null,
    variant_id: localizedProduct?.variant_id || null,
    template_id: localizedProduct?.template_id || null,
    categories: Array.isArray(localizedProduct?.categories)
      ? localizedProduct.categories.slice(0, 5)
      : [],
    lang: currentLang,
    source: "product_sheet",
    qty: 1,
    price: Number(localizedProduct?.price ?? 0),
  });

  onAddToCart?.({
    ...localizedProduct,
    id: vid,
  });
}

  const goToIndex = useCallback(
    (next) => {
      const i = Math.max(0, Math.min(images.length - 1, next));
      setImgIndex(i);

      const el = sliderRef.current;
      if (!el) return;

      const w = el.clientWidth || 1;
      el.scrollTo({ left: i * w, behavior: "smooth" });
    },
    [images.length]
  );

  const prevImage = useCallback(() => {
    if (images.length <= 1) return;
    goToIndex((imgIndex - 1 + images.length) % images.length);
  }, [goToIndex, imgIndex, images.length]);

  const nextImage = useCallback(() => {
    if (images.length <= 1) return;
    goToIndex((imgIndex + 1) % images.length);
  }, [goToIndex, imgIndex, images.length]);

  const onSliderScroll = useCallback(() => {
    const el = sliderRef.current;
    if (!el) return;
    const w = el.clientWidth || 1;
    const i = Math.round(el.scrollLeft / w);
    if (i !== imgIndex) setImgIndex(i);
  }, [imgIndex]);

  useEffect(() => {
    function onKey(e) {
      if (e.key === "Escape") onClose?.();
      if (images.length > 1) {
        if (e.key === "ArrowLeft") prevImage();
        if (e.key === "ArrowRight") nextImage();
      }
    }

    if (mounted) {
      document.addEventListener("keydown", onKey);
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [mounted, onClose, images.length, prevImage, nextImage]);

  if (!mounted) return null;

  const overlayClass = `psOverlay ${open && !closing ? "isOpen" : "isClosing"}`;
  const price = formatPriceUAH(localizedProduct?.price);
  const cats = localizedProduct?.categories || [];

  const abv = localizedProduct?.abv || getBeerTagValue(localizedProduct, "abv");
const ibu = localizedProduct?.ibu || getBeerTagValue(localizedProduct, "ibu");

  const sheetStyle = dragY
    ? {
        transform: `translateY(${dragY}px)`,
        transition: "none",
      }
    : undefined;

   function getBeerTagValue(product, key) {
  const target = String(key || "").toLowerCase();

  const arraySources = [
    product?.tags,
    product?.tag_names,
    product?.product_tags,
    product?.attributes,
    product?.attribute_values,
  ];

  const textSources = [
    product?.name,
    product?.name_uk,
    product?.name_en,
    product?.description_text,
    product?.description_uk,
    product?.description_en,
  ];

  const flat = [
    ...arraySources
      .filter(Array.isArray)
      .flat()
      .map((x) => {
        if (typeof x === "string") return x;

        return (
          x?.name ||
          x?.display_name ||
          x?.value ||
          x?.label ||
          x?.title ||
          ""
        );
      }),
    ...textSources,
  ].filter(Boolean);

  const found = flat.find((x) => String(x).toLowerCase().includes(target));

  if (!found) return null;

  const match = String(found).match(/(\d+(?:[.,]\d+)?)/);
  return match ? match[1].replace(",", ".") : null;
}
  return (
    <>
      <div
        className={overlayClass}
        style={{ paddingTop: topOffset }}
        onClick={onClose}
        role="dialog"
        aria-modal="true"
      >
        <div
          className="psSheet"
          ref={sheetRef}
          onClick={(e) => e.stopPropagation()}
          style={sheetStyle}
        >
          <div
            className="psHandleWrap"
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerCancel}
          >
            <div className="psHandle" />
          </div>

          <div
            className="psTopBar"
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerCancel}
          >
            <div className="psTopLeft">
              <div className="psTitle">
                {localizedProduct?.name || t("fallback_product")}
              </div>

              {!!cats.length && (
                <div className="psCats">
                  <div className="psBreadcrumb">
                    {cats.slice(0, 2).map((c, i, arr) => (
                      <span
                        key={`${c}-${i}`}
                        className={`psBreadcrumbItem ${
                          i === arr.length - 1 ? "isActive" : ""
                        }`}
                      >
                        {c}
                        {i !== arr.length - 1 && (
                          <span className="psBreadcrumbSep"> / </span>
                        )}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="psActions">
              <button
                type="button"
                className={`psIconBtn ${isFavorite ? "isActive" : ""}`}
                onClick={() => {
  trackEvent("favorite_added", {
    product_id: localizedProduct?.product_id || localizedProduct?.id || null,
    product_name: localizedProduct?.name || null,
    variant_id: localizedProduct?.variant_id || null,
    categories: Array.isArray(localizedProduct?.categories)
      ? localizedProduct.categories.slice(0, 5)
      : [],
    lang: currentLang,
    source: "product_sheet",
  });

  onToggleFavorite?.(localizedProduct);
}}
                aria-label={t("favorite")}
                title={t("favorite")}
              >
                <FiHeart className="psIcon" />
              </button>

              <button
                type="button"
                className="psIconBtn"
                onClick={onClose}
                aria-label={t("close")}
                title={t("close")}
              >
                <FiX className="psIcon" />
              </button>
            </div>
          </div>

          <div
            className="psContent"
            ref={contentRef}
            style={{ paddingBottom: `calc(140px + env(safe-area-inset-bottom, 0px))` }}
          >
            <div className="psBody">
              {images.length > 0 && (
                <div className="psMedia">
                  {images.length === 1 ? (
                    <div className="psProductImageWrap">
  <img
    className="psProductImage"
    src={images[0].url}
    alt={localizedProduct?.name || t("fallback_product")}
  />

  {(abv || ibu) && (
  <div className="psProductTags">
    {ibu && (
      <span className="psTag" data-label="IBU">
        <span className="psTagValue">{ibu}</span>
      </span>
    )}

    {abv && (
      <span className="psTag" data-label="ABV">
        <span className="psTagValue">{abv}%</span>
      </span>
    )}
  </div>
)}
</div>
                  ) : (
                    <div className="psCarousel" aria-label={t("gallery")}>
  {(abv || ibu) && (
    <div className="psProductTags psProductTagsCarousel">
      {ibu && (
        <span className="psTag" data-label="IBU">
          <span className="psTagValue">{ibu}</span>
        </span>
      )}

      {abv && (
        <span className="psTag" data-label="ABV">
          <span className="psTagValue">{abv}%</span>
        </span>
      )}
    </div>
  )}

  <div
    className="psSlider"
    ref={sliderRef}
    onScroll={onSliderScroll}
    role="group"
    aria-roledescription="carousel"
    aria-label={t("product_photos")}
  >
                        {images.map((it) => (
                          <div className="psSlide" key={it.id}>
                            <img
                              className="psProductImage"
                              src={it.url}
                              alt={localizedProduct?.name || t("fallback_product")}
                            />
                          </div>
                        ))}
                      </div>

                     

                      <div className="psDots" onClick={(e) => e.stopPropagation()}>
                        {images.map((it, i) => (
                          <button
                            key={it.id}
                            type="button"
                            className={`psDot ${i === imgIndex ? "isActive" : ""}`}
                            onClick={() => goToIndex(i)}
                            aria-label={t("photo_n", { count: i + 1 })}
                            title={t("photo_n", { count: i + 1 })}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              <ProductTabs
                activeTab={activeTab}
                onChangeTab={setActiveTab}
                product={localizedProduct}
                reviews={reviews}
                reviewsLoading={reviewsLoading}
                onOpenLeaveComment={handleOpenLeaveComment}
                isLoggedIn={!!user}
              />

              <AlternativeProductsBlock
                excludeProductId={
                  localizedProduct?.variant_id || localizedProduct?.id
                }
                onOpen={(p) => onOpenProduct?.(p)}
                onAdd={(p) => onAddToCart?.(p)}
              />
            </div>
          </div>

          <div className="psBuyBar" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="psAddBtn"
              onClick={handleBuyClick}
              disabled={
                !(
                  localizedProduct?.variant_id ||
                  localizedProduct?.product_id ||
                  localizedProduct?.id
                )
              }
            >
              <span className="psAddBtnLabel">{t("buy")}</span>
              <span className="psAddBtnPrice">{price}</span>
            </button>
          </div>
        </div>
      </div>

      <LeaveCommentSheet
        open={isLeaveCommentOpen}
        onClose={() => setIsLeaveCommentOpen(false)}
        product={localizedProduct}
        onCreated={async () => {
          await loadReviews();
        }}
      />
    </>
  );
}